/**
 * Portalweite Suche — Ergebnis-Ansicht (PS-4, 2026-09-09).
 *
 * Route /suche?q=… — kategorisiert die Funde nach Typ, jede Kategorie ist
 * eine eigene Lazy-Load-Einheit: /portal/search/types liefert die fuer den
 * User sichtbaren Kategorien, danach laeuft pro Kategorie ein paralleler
 * Request; gerendert wird sobald die jeweilige Antwort da ist. "Mehr
 * anzeigen" laedt per offset nach. Bewusst apiFetch statt api.get — der
 * Read-Cache wuerde sonst jede Query cachen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Loader2, Box } from 'lucide-react';
import { apiFetch } from '@onvice/portal-shell-api';
import { LucideByName } from '@onvice/portal-ui';
import type { SearchTypeInfo, SearchTypeResult, SearchResultItem } from '@onvice/shared-types';

interface ApiResp<T> { success: boolean; data: T; error?: string }

type CategoryState = {
  info: SearchTypeInfo;
  status: 'loading' | 'done' | 'error' | 'loading-more';
  items: SearchResultItem[];
  total: number;
};

const INITIAL_LIMIT = 5;
const MORE_LIMIT = 10;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') ?? '').trim();
  const [term, setTerm] = useState(q);
  const [types, setTypes] = useState<SearchTypeInfo[] | null>(null);
  const [typesError, setTypesError] = useState(false);
  const [categories, setCategories] = useState<Record<string, CategoryState>>({});
  // Laufende Suche identifizieren — spaete Antworten alter Suchen verwerfen.
  const runRef = useRef(0);

  useEffect(() => { setTerm(q); }, [q]);

  // Sichtbare Kategorien einmal laden
  useEffect(() => {
    let cancelled = false;
    apiFetch<ApiResp<SearchTypeInfo[]>>('/portal/search/types')
      .then((r) => { if (!cancelled) setTypes(r.data ?? []); })
      .catch(() => { if (!cancelled) { setTypes([]); setTypesError(true); } });
    return () => { cancelled = true; };
  }, []);

  // Pro Suchbegriff: alle Kategorien parallel anfragen
  useEffect(() => {
    if (!types || q.length < 2) { setCategories({}); return; }
    const run = ++runRef.current;
    const initial: Record<string, CategoryState> = {};
    for (const info of types) {
      initial[info.type] = { info, status: 'loading', items: [], total: 0 };
    }
    setCategories(initial);
    for (const info of types) {
      apiFetch<ApiResp<SearchTypeResult>>(
        `/portal/search?q=${encodeURIComponent(q)}&type=${encodeURIComponent(info.type)}&limit=${INITIAL_LIMIT}`,
      )
        .then((r) => {
          if (runRef.current !== run) return;
          setCategories((prev) => ({
            ...prev,
            [info.type]: { info, status: 'done', items: r.data?.items ?? [], total: r.data?.total ?? 0 },
          }));
        })
        .catch(() => {
          if (runRef.current !== run) return;
          setCategories((prev) => ({ ...prev, [info.type]: { info, status: 'error', items: [], total: 0 } }));
        });
    }
  }, [types, q]);

  const loadMore = useCallback((type: string) => {
    const cat = categories[type];
    if (!cat || cat.status === 'loading-more') return;
    const run = runRef.current;
    setCategories((prev) => ({ ...prev, [type]: { ...prev[type]!, status: 'loading-more' } }));
    apiFetch<ApiResp<SearchTypeResult>>(
      `/portal/search?q=${encodeURIComponent(q)}&type=${encodeURIComponent(type)}&limit=${MORE_LIMIT}&offset=${cat.items.length}`,
    )
      .then((r) => {
        if (runRef.current !== run) return;
        setCategories((prev) => {
          const cur = prev[type];
          if (!cur) return prev;
          return {
            ...prev,
            [type]: {
              ...cur,
              status: 'done',
              items: [...cur.items, ...(r.data?.items ?? [])],
              total: r.data?.total ?? cur.total,
            },
          };
        });
      })
      .catch(() => {
        if (runRef.current !== run) return;
        setCategories((prev) => (prev[type] ? { ...prev, [type]: { ...prev[type]!, status: 'done' } } : prev));
      });
  }, [categories, q]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = term.trim();
    if (next.length >= 2) setSearchParams({ q: next });
  };

  const ordered = useMemo(() => (types ?? []).map((t) => categories[t.type]).filter((c): c is CategoryState => !!c), [types, categories]);
  const anyLoading = ordered.some((c) => c.status === 'loading');
  const hitCategories = ordered.filter((c) => c.items.length > 0);
  const nothingFound = q.length >= 2 && !anyLoading && ordered.length > 0 && hitCategories.length === 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-xl font-semibold mb-4">Suche</h1>

      <form onSubmit={submit} role="search" className="mb-6">
        <div className="relative">
          <input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Kunden, Aufgaben, Berichte, Dokumente …"
            aria-label="Suchtext"
            autoFocus={q.length === 0}
            className="w-full rounded-lg border border-default bg-card pl-4 pr-12 py-2.5 text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary"
            title="Suchen"
            aria-label="Suchen"
          >
            <Search size={18} />
          </button>
        </div>
        {q.length > 0 && q.length < 2 && (
          <p className="mt-2 text-sm text-muted-foreground">Bitte mindestens 2 Zeichen eingeben.</p>
        )}
      </form>

      {typesError && (
        <p className="text-sm text-rose-500">Die Suche ist derzeit nicht verfügbar. Bitte später erneut versuchen.</p>
      )}

      {q.length < 2 && !typesError && (
        <p className="text-sm text-muted-foreground">
          Suchbegriff eingeben und mit Enter oder der Lupe bestätigen — die Ergebnisse erscheinen hier nach Typ gruppiert.
        </p>
      )}

      {nothingFound && (
        <p className="text-sm text-muted-foreground">Keine Treffer für „{q}".</p>
      )}

      <div className="space-y-6">
        {ordered.map((cat) => {
          if (cat.status === 'error') return null;
          if (cat.status === 'done' && cat.items.length === 0) return null;
          return (
            <section key={cat.info.type} aria-label={cat.info.label}>
              <div className="flex items-center gap-2 mb-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                <LucideByName name={cat.info.icon} fallback={Box} size={16} className="shrink-0" />
                <span>{cat.info.label}</span>
                {cat.status !== 'loading' && (
                  <span className="text-xs font-normal normal-case tracking-normal">({cat.total})</span>
                )}
              </div>

              {cat.status === 'loading' ? (
                <div className="rounded-lg border border-default bg-card p-3 space-y-2 animate-pulse">
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ) : (
                <ul className="rounded-lg border border-default bg-card divide-y divide-default overflow-hidden">
                  {cat.items.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        className="block px-4 py-2.5 hover:bg-primary/5 focus:outline-none focus:bg-primary/10"
                      >
                        <div className="text-sm font-medium text-foreground">{item.title}</div>
                        {item.subtitle && (
                          <div className="text-xs text-muted-foreground mt-0.5">{item.subtitle}</div>
                        )}
                        {item.snippet && (
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            <Snippet text={item.snippet} />
                          </div>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {(cat.status === 'done' || cat.status === 'loading-more') && cat.total > cat.items.length && (
                <button
                  type="button"
                  onClick={() => loadMore(cat.info.type)}
                  disabled={cat.status === 'loading-more'}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline disabled:opacity-60"
                >
                  {cat.status === 'loading-more' && <Loader2 size={14} className="animate-spin" />}
                  Mehr anzeigen ({cat.total - cat.items.length} weitere)
                </button>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Rendert einen ts_headline-Snippet-String: NUR <mark>…</mark> wird als
 * Markup interpretiert, alles andere bleibt reiner Text (kein
 * dangerouslySetInnerHTML — Suchtreffer sind Nutzdaten).
 */
function Snippet({ text }: { text: string }) {
  const parts = text.split(/<mark>|<\/mark>/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1
          ? <mark key={i} className="bg-amber-200/60 dark:bg-amber-500/30 rounded px-0.5">{part}</mark>
          : <span key={i}>{part}</span>,
      )}
    </>
  );
}
